import { Box, Paper, Stack, Typography } from '@mui/material';
import { vyDate, withDefault } from '@vy/utils';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams } from 'react-router-dom';
import { VyLayout } from '../../../components/new-design/layout/VyLayout';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withPrefetch } from '../../../components/hocs';
import { prefetchMeters } from '../../../hooks/requests/use-meters/use-meters';
import { useAccount } from '../../../hooks/requests/use-account';
import { useMeasurementsStats } from '../../../hooks/requests/use-measurements-stats';
import { useFromDayParam } from '../../../hooks/search-params/use-from-day-param';
import { useToDayParam } from '../../../hooks/search-params/use-to-day-param';
import { isNotProduction } from '../../../utils/env-indicator';
import { DataMeteringContent } from './data-metering-components/DataMeteringContent';
import { DataMeteringToolbarContent } from './data-metering-components/DataMeteringToolbarContent';
import { useDataMeteringParamsIds } from './data-metering-hooks/use-data-metering-params-ids';
import { useTablePagination } from '../../../hooks/use-table-pagination';

const DataMeteringPage: React.FC = () => {
  const { data } = useAccount();
  const manualMeteringAllowed = !!data?.account.settings.meteringSettings?.manualMetering;

  const [selectedMeasurementIds, setSelectedMeasurementIds] = useState<string[]>([]);
  const [searchParams] = useSearchParams();

  const defaultFromDay = vyDate().startOf('day');
  const defaultToDay = vyDate().endOf('day');

  const [paramsDayFrom] = useFromDayParam(defaultFromDay);
  const [paramsDayTo] = useToDayParam(defaultToDay);

  const fromDay = paramsDayFrom ?? defaultFromDay;
  const toDay = paramsDayTo ?? defaultToDay;

  const startTime = fromDay.utc(true).toDate();
  const endTime = toDay.utc(true).toDate();

  const { customersIds, metersIds, eventNames } = useDataMeteringParamsIds(searchParams);

  const { page, pageSize, onPageChange, onPageSizeChange } = useTablePagination();
  const { data: measurementsStatsResponse, isLoading: isLoadingMeasurementsStats } = useMeasurementsStats({
    startTime,
    endTime,
    customersIds,
    eventNames,
    metersIds,
    page,
    pageSize,
    mode: null,
  });

  const measurementsStats = withDefault(measurementsStatsResponse?.items, []);
  const totalCount = withDefault(measurementsStatsResponse?.totalCount, 0);
  const totalValue = withDefault(measurementsStatsResponse?.totalValue, 0);

  return (
    <>
      <Helmet>
        <title>Data Metering</title>
      </Helmet>
      <VyLayout
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Stack direction="row" spacing={2} sx={{
          alignItems: 'center'
        }}>
          <Typography
            sx={{
              fontSize: 24,
              padding: 1.5,
              flex: 1
            }}>
            Usage
          </Typography>

          {isNotProduction() && (
            <Link className="text-blue-600 underline hover:text-blue-800" to="admin">
              Admin
            </Link>
          )}
        </Stack>

        <Paper
          variant="outlined"
          elevation={1}
          sx={{
            borderRadius: '12px',
            height: '100%',
            display: 'flex',
            position: 'relative',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <DataMeteringToolbarContent
            deleteVisible={manualMeteringAllowed}
            selectedMeasurementIds={selectedMeasurementIds}
            totalMeasurementsCount={totalCount}
            eventNames={withDefault(eventNames, [])}
          />

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <DataMeteringContent
              editingAllowed={manualMeteringAllowed}
              onMeasurementSelectionChange={setSelectedMeasurementIds}
              selectedMeasurementsIds={selectedMeasurementIds}
              measurementsStats={measurementsStats}
              isLoading={isLoadingMeasurementsStats}
              totalCount={totalCount}
              totalValue={totalValue}
              page={page}
              pageSize={pageSize}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </Box>
        </Paper>
      </VyLayout>
    </>
  );
};

const DataMeteringPageWithSuspenseBoundary = withGlobalSearchParams(withPrefetch(DataMeteringPage, prefetchMeters));
export { DataMeteringPageWithSuspenseBoundary as DataMeteringPage };
